//#region imports

import { RatingPayload } from '../../models/payloads/rating.payload';
import { CourseService } from '../../services/course/course.service';
import { Component, Input, OnInit } from '@angular/core';
import { ModalService } from '../../services/modal/modal.service';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { RatingsProxy } from 'src/app/models/proxies/course-ratings.proxy';
import { TrackService } from '../../services/track/track.service';

//#endregion

@Component({
  selector: 'uni-rating-modal',
  templateUrl: './rating-modal.component.html',
  styleUrls: ['./rating-modal.component.scss']
})
export class RatingModalComponent implements OnInit {

  //#region constructor

  constructor(
    private readonly modalService: ModalService,
    private readonly courseService: CourseService,
    private readonly trackService: TrackService,
    private readonly toast: NzNotificationService,
  ) { }

  //#endregion

  //#region public Properties

  @Input()
  public modalId: string;

  @Input()
  public userId: string;

  @Input()
  public entityId: string;

  @Input()
  public loggedUserRating: RatingsProxy;

  @Input()
  public courseId: string;

  @Input()
  public trackId: string;

  @Input()
  public trackName: string;

  @Input()
  public emitValue: () => void = () => void 0;

  public isSendingRating: boolean = false;

  public ratingPayload: RatingPayload = {
    id: '',
    score: 0,
    comment: '',
    userId: '',
    courseId: '',
    trackId: '',
  };

  public didUserTriedToSendScoreZero: boolean = false;

  public characterCounter: number = 0;

  //#endregion

  //#region public methods

  public get isTrackRatingMode(): boolean {
    return !!this.trackId;
  }

  public get modalTitle(): string {
    if (!this.isTrackRatingMode)
      return 'Como você classificaria este curso?';

    if (!this.trackName)
      return 'Feedback da trilha';

    return `Feedback da trilha: ${this.trackName}`;
  }

  public get ratingSectionTitle(): string {
    return this.isTrackRatingMode ? 'Avaliação geral da trilha' : 'Selecione uma classificação';
  }

  public get commentPlaceholder(): string {
    return this.isTrackRatingMode
      ? 'O que você mais gostou? O que podemos melhorar?'
      : 'Descreva como foi sua experiência pessoal com este curso.';
  }

  public get submitButtonText(): string {
    return this.isTrackRatingMode ? 'Enviar feedback' : 'Salvar e continuar';
  }

  public ngOnInit(): void {
    this.setupPayload();

    this.characterCounter = (this.ratingPayload.comment || '').length;
  }

  public closeModal(): void {
    if (this.emitValue) this.emitValue();

    this.modalService.destroy(this.modalId);
  }

  public async sendRating(): Promise<void> {
    if (this.ratingPayload.score === 0) {
      this.didUserTriedToSendScoreZero = true;
      return;
    }

    this.isSendingRating = true;

    const requisitionAnswer: [boolean, string?] = await this.getRequisitionAnswer();

    this.isSendingRating = false;

    if (!requisitionAnswer[0]) {
      this.closeModal();
      return void this.toast.error('Error', requisitionAnswer[1], { nzKey: 'key' });
    }

    this.toast.success(
      'Sucesso!',
      this.isTrackRatingMode
        ? 'Seu feedback da trilha foi enviado com sucesso!'
        : 'Sua avaliação foi adicionada! Muito obrigado!',
    );

    this.closeModal();
  }

  public valueChange(): void {
    this.characterCounter = (this.ratingPayload.comment || '').length;
  }

  private setupPayload(): void {
    this.ratingPayload.userId = this.userId;
    this.ratingPayload.courseId = this.isTrackRatingMode ? '' : this.courseId;
    this.ratingPayload.trackId = this.isTrackRatingMode ? this.trackId : '';

    if (this.loggedUserRating) {
      this.ratingPayload.id = this.loggedUserRating.id;
      this.ratingPayload.comment = this.loggedUserRating.comment;
      this.ratingPayload.score = this.loggedUserRating.score;
    }
  }

  private async getRequisitionAnswer(): Promise<[boolean, string?]> {
    const service = this.isTrackRatingMode ? this.trackService : this.courseService;

    return this.loggedUserRating
      ? await service.editRating(this.ratingPayload)
      : await service.sendRating(this.ratingPayload);
  }

  //#endregion

}
